import React, { useEffect, useState } from 'react';
import Icon from '@sabre/spark-react-core/icon';
import { array, bool, string } from 'prop-types';
import AgGridReactTable from '../../ui-components/ag-gird-table/ag-grid-react-table.jsx';
import Loader from '../../ui-components/loader/loader.jsx';
import { SEARCH_STATUS, SIZE } from '../../utils/constants.js';
import { NOTIFICATION_MESSAGES } from '../../utils/notification-messages.js';
import { toastSuccess } from '../../utils/toasts.js';
import {
   getRecord3DetailsTableHeaders,
   getRecord3TableData,
} from './__helpers__/functions.js';
import { SEGMENTS_TABLE_HEADERS } from './__helpers__/tableHeaders.js';
import useSearch from './hooks/useSearch.js';

const defaultTableData = {
   mainTable: [],
   segmentsTable: [],
   record3Table: [],
};

function FootnotesAndRulesTable({
   searchQuery,
   isAbortSearch,
   tableHeaders,
   noResultMessage,
}) {
   const [tableData, setTableData] = useState(defaultTableData);
   const { isLoading, responseData, searchStatus } = useSearch(
      searchQuery,
      isAbortSearch
   );
   const [selectedRows, setSelectedRows] = useState(['1']);

   const totalRowCount = tableData.mainTable.length;
   const rowSelectionIndicator =
      selectedRows.length > 1
         ? `${selectedRows.length} records selected`
         : `Record ${selectedRows[0]} selected`;

   const record3TableHeaders = getRecord3DetailsTableHeaders(
      tableData.record3Table[0]
   );

   const updateSelectedRows = (selectedNodes) => {
      const selectedRowIndices = selectedNodes?.map(
         (node) => node.rowIndex + 1
      );
      setSelectedRows(selectedRowIndices);
   };

   const handleMainTableRowClick = (selectedRows) => {
      if (selectedRows?.length > 1) {
         setTableData((previousData) => ({
            ...previousData,
            segmentsTable: [],
            record3Table: [],
         }));
      } else {
         const updatedSegments = selectedRows?.[0]?.data.segments || [];

         const updatedRecord3s = getRecord3TableData(
            selectedRows?.[0]?.data?.segments[0]?.record3Details
         );

         setTableData((previousData) => ({
            ...previousData,
            segmentsTable: updatedSegments,
            record3Table: updatedRecord3s,
         }));
      }

      updateSelectedRows(selectedRows);
   };

   const handleSegmentsTableRowClick = (selectedRows) => {
      if (!selectedRows?.length) return;

      if (selectedRows.length > 1) {
         setTableData((previousData) => ({
            ...previousData,
            record3Table: [],
         }));
         return;
      }

      const updatedRecord3s = getRecord3TableData(
         selectedRows[0].data.record3Details
      );
      setTableData((previousData) => ({
         ...previousData,
         record3Table: updatedRecord3s,
      }));
   };

   useEffect(() => {
      if (isLoading) {
         setTableData(defaultTableData);
         setSelectedRows(['1']);
      }

      if (!responseData?.length > 0) return;

      setTableData((tableData) => ({
         mainTable: responseData,
         segmentsTable:
            tableData?.segmentsTable?.length === 0
               ? responseData[0]?.segments
               : tableData?.segmentsTable,
         record3Table:
            tableData?.record3Table?.length === 0
               ? getRecord3TableData(
                    responseData[0]?.segments?.[0]?.record3Details
                 )
               : tableData?.record3Table,
      }));
   }, [responseData, isLoading]);

   useEffect(() => {
      if (searchStatus === SEARCH_STATUS.COMPLETED) {
         toastSuccess(NOTIFICATION_MESSAGES.SEARCH_COMPLETED);
      }
   }, [searchStatus]);

   return (
      <section className='footnotes-and-rules-table'>
         {isLoading ? (
            <div className='loader'>
               <Loader size={SIZE.SMALL} />
            </div>
         ) : tableData.mainTable.length > 0 ? (
            <>
               <div className='h-half'>
                  <AgGridReactTable
                     rowData={tableData.mainTable}
                     headers={tableHeaders}
                     onSelectedRowsChange={handleMainTableRowClick}
                     enableMultiRowSelection
                     enableRowIds
                     rememberSelectedRow
                  />
               </div>

               <div className='h-half spark-flex'>
                  <div className='h-full spark-mar-r-.5 segment-table'>
                     <AgGridReactTable
                        rowData={tableData.segmentsTable}
                        headers={SEGMENTS_TABLE_HEADERS}
                        onSelectedRowsChange={handleSegmentsTableRowClick}
                        enableMultiRowSelection
                        autoSizeColumns={false}
                        selectFirstRowByDefault
                     />
                  </div>
                  <div className='h-full flex-grow-1'>
                     <AgGridReactTable
                        rowData={tableData.record3Table}
                        headers={record3TableHeaders}
                        selectFirstRowByDefault={false}
                     />
                  </div>
               </div>

               <p className='spark-bold spark-flex justify-between spark-pad-t-.5 row-selection-info'>
                  <span>
                     {rowSelectionIndicator} (Total {totalRowCount})
                  </span>
                  {searchStatus === SEARCH_STATUS.ABORTED ? (
                     <Icon
                        name='close-circle'
                        className='spark-danger spark-mar-l-.5'
                        size='md'
                        style='fill'
                        label='Aborted'
                        title='Aborted'
                     />
                  ) : searchStatus === SEARCH_STATUS.IN_PROGRESS ? (
                     <span
                        className='progress-indicator spark-mar-l-.5'
                        title='Loading...'
                        label='Loading...'
                        aria-label='Loading...'
                     />
                  ) : (
                     ''
                  )}
               </p>
            </>
         ) : (
            <p className='spark-bold grid place-items-center h-full text-lg'>
               {noResultMessage}
            </p>
         )}
      </section>
   );
}

FootnotesAndRulesTable.propTypes = {
   searchQuery: array,
   isAbortSearch: bool,
   tableHeaders: array,
   noResultMessage: string,
};

export default FootnotesAndRulesTable;
